import { useMutation } from '@tanstack/react-query';
import {
  SelfShiftResponseSchema,
  type SelfShiftPatchInput,
  type SelfShiftResponse,
} from '@magimp/shared';
import { useApiClient } from '../api/context.js';

/**
 * @aiPurpose `PATCH /users/me/shift` — the authenticated `logist_cn`
 *   flipping their own `isOnShift` flag. Returns
 *   `{ isOnShift, isLastOnShift }` recomputed in the same DB transaction.
 * @aiInvariant the endpoint is fixed (no `id` argument); the caller's
 *   identity is read from `jwt.sub` server-side. Never accept a user id
 *   from path or body. The hook does NOT invalidate `['user', 'me']` —
 *   `/auth/me` is fetched imperatively by `useAuthBootstrap`, not via
 *   React Query, so an invalidation here would no-op. The caller is
 *   responsible for projecting the response into whatever auth store
 *   the consuming app uses (web: `useAuthStore.setUserShift`).
 * @aiSecurity non-`logist_cn` callers receive `FORBIDDEN` from the
 *   service layer; the hook surfaces the error verbatim.
 */
export function usePatchSelfShift() {
  const client = useApiClient();
  return useMutation<SelfShiftResponse, unknown, SelfShiftPatchInput>({
    mutationFn: (input) =>
      client.patch<SelfShiftResponse>('/users/me/shift', input, {}, SelfShiftResponseSchema),
  });
}
